import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUp, Printer } from "lucide-react";

// -----------------------------------------------------------------------------
// ENCORE THEATRE & GALLERY — History
// Birmingham, Alabama · Founded 2018
// -----------------------------------------------------------------------------

const SECTIONS = [
  { id: "founding", num: "01", title: "Founding & Establishment", year: "2018–2019" },
  { id: "identity", num: "02", title: "Building an Artistic Identity", year: "2019–2020" },
  { id: "education", num: "03", title: "Arts Education & Youth" },
  { id: "covid", num: "04", title: "The Pandemic Chapter", year: "2020" },
  { id: "rebuild", num: "05", title: "Reopening & Growth", year: "2021–2024" },
  { id: "original", num: "06", title: "Original Works" },
  { id: "guest", num: "07", title: "Guest Artists & Reach" },
  { id: "transformative", num: "08", title: "A Home for Transformative Art" },
  { id: "downtown", num: "09", title: "Downtown Relocation", year: "2025" },
  { id: "jazzhall", num: "10", title: "Alabama Jazz Hall Partnership", year: "2025–Present" },
  { id: "collaboration", num: "11", title: "Collaboration Across Birmingham", year: "2025–Present" },
  { id: "season-eight", num: "12", title: "Season Eight: Still Becoming", year: "2026–2027" },
  { id: "today", num: "13", title: "Encore Today" },
  { id: "timeline", num: "14", title: "Institutional Timeline" },
];

const TIMELINE_ENTRIES = [
  { year: "2018", title: "Founding", body: "Encore Theatre & Gallery is founded in Birmingham, Alabama, creating a theatrical home dedicated to expanding opportunities for African American artists and stories." },
  { year: "2018–2019", title: "Inaugural Season", body: "Encore presents its first full season of productions, including for colored girls who have considered suicide / when the rainbow is enuf, Happy Ending and the original work Tiny Tim. Contemporary reporting later documented that Encore presented five major productions during its first season and drew strong audiences." },
  { year: "2019", title: "Encore School of Arts & Theatre", body: "The organization incorporates as the Encore School of Arts and Theatre, expanding its institutional focus to include education, training, new-work development and interdisciplinary programming." },
  { year: "Early Years", title: "Acting Up", body: "Encore establishes Acting Up, its youth theatre camp, as part of its developing commitment to arts education and young artists." },
  { year: "2020", title: "Monologue Mondays", body: "During the COVID-19 shutdown, Encore launches Monologue Mondays, bringing together performers from Birmingham and other cities through virtual performance. The series receives a special video acknowledgment from legendary actress Marla Gibbs." },
  { year: "2021–2024", title: "Rebuilding & Growth", body: "Encore resumes live programming and continues developing productions, original works, educational initiatives, special events and opportunities for local artists." },
  { year: "Through the Years", title: "Expanding Artistic Reach", body: "Encore programming welcomes nationally recognized artists including Ann Nesby, Terrell Carter, Willie Taylor, Dondria Nicole and Ray Lavender, alongside Birmingham artists, emerging performers and young people developing their craft." },
  { year: "2025", title: "Downtown Birmingham", body: "Encore relocates from Roebuck to 1604 Second Avenue North, establishing its new home in downtown Birmingham. Contemporary reporting documented the move as part of renewed activity within Birmingham's downtown theatre district." },
  { year: "2025", title: "Alabama Jazz Hall of Fame Partnership", body: "Encore launches a partnership with the Alabama Jazz Hall of Fame, expanding Encore's theatrical programming to the historic Carver Theatre. The Carver is home to the Jazz Hall and occupies an important place within Birmingham's Black cultural and entertainment history." },
  { year: "2025–Present", title: "Expanded Collaboration", body: "Encore continues developing relationships with Birmingham arts organizations, artists, independent producers and cultural institutions." },
  { year: "2026", title: "ShowUp Drama Camp", body: "Encore's youth camp, originally known as Acting Up, continues its evolution as ShowUp Drama Camp, carrying the organization's educational mission into its next chapter." },
  { year: "2026", title: "Birmingham Festival Theatre Collaboration", body: "Encore's relationship with Birmingham Festival Theatre expands opportunities for shared programming, artists, venues, audiences and organizational resources." },
  { year: "2026–2027", title: "Season Eight: Still Becoming", body: "Encore enters its eighth season under the theme STILL BECOMING, continuing its work as a Birmingham theatre, educational organization, producing institution and Home for Transformative Art." },
];

export default function History() {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const opts = { rootMargin: "-30% 0px -60% 0px", threshold: 0 };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActive(e.target.id);
      });
    }, opts);
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="history-page" className="pt-28 pb-24 bg-stone-950 text-stone-300 print:bg-white print:text-black">
      {/* Return + print bar */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-12 flex items-center justify-between gap-4 print:hidden">
        <Link
          to="/about"
          data-testid="history-back"
          className="inline-flex items-center gap-2 font-overline text-[0.7rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors"
        >
          <ArrowLeft size={14} /> Back to About
        </Link>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="history-print"
          className="inline-flex items-center gap-2 font-overline text-[0.65rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors border border-white/15 hover:border-[#DE9E48] rounded-full px-4 py-2"
        >
          <Printer size={13} strokeWidth={1.75} /> Print / Save PDF
        </button>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-20 md:mb-24">
        <p className="font-overline text-[0.7rem] tracking-[0.28em] text-[#DE9E48] mb-5">
          — Since 2018 · Birmingham, Alabama
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tighter text-[#FDFBF7] mb-8 max-w-4xl">
          Our <span className="italic text-[#DE9E48]">History.</span>
        </h1>
        <p className="font-body italic text-stone-300 text-lg md:text-xl max-w-2xl leading-relaxed">
          Eight years of building a Birmingham cultural home for bold
          storytelling, Black artistry and community gathering.
        </p>
      </section>

      {/* Body layout — sticky TOC + prose */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Sticky sidebar TOC */}
        <aside className="lg:col-span-3 print:hidden">
          <div className="lg:sticky lg:top-28">
            <p className="font-overline text-[0.62rem] tracking-[0.24em] text-stone-500 mb-5">
              — Contents
            </p>
            <nav data-testid="history-toc">
              <ul className="space-y-2.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      data-testid={`history-toc-${s.id}`}
                      className={`group flex items-baseline gap-3 py-1 font-body text-sm leading-tight transition-colors ${
                        active === s.id
                          ? "text-[#DE9E48]"
                          : "text-stone-400 hover:text-[#FDFBF7]"
                      }`}
                    >
                      <span className="font-overline text-[0.6rem] tracking-[0.2em] text-stone-600 tabular-nums w-6 flex-shrink-0">
                        {s.num}
                      </span>
                      <span className="tracking-tight">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Prose column */}
        <main className="lg:col-span-9 space-y-24 md:space-y-28 leading-[1.75] print:space-y-16">
          <Section id="founding" num="01" title="Founding & Establishment" year="2018–2019">
            <Prose>
              Encore Theatre &amp; Gallery was founded in Birmingham, Alabama,
              in 2018 by playwright, director and producer{" "}
              <span className="text-[#FDFBF7] font-medium">Marc Raby</span>.
              The theatre was established to expand opportunities for African
              American artists and to create a consistent cultural home where
              stories from the African diaspora could be developed, presented
              and celebrated.
            </Prose>
            <Prose>
              From its beginning, Encore was envisioned as more than a producing
              theatre. The organization sought to create a place where artists
              could develop their craft, audiences could gather, new voices
              could emerge and African American stories could occupy a central
              and sustained place within Birmingham's theatrical landscape.
            </Prose>
            <Prose>
              The concept challenged assumptions about the audience and artistic
              community available to support such a theatre. Before Encore
              opened, questions were raised about whether Birmingham could
              sustain an entire theatrical season centered on Black performers,
              stories and audiences.
            </Prose>
            <PullQuote>Encore's inaugural season helped answer those questions.</PullQuote>
            <Prose>
              The company presented five major productions during its first
              season, including Ntozake Shange's{" "}
              <em className="text-[#FDFBF7] not-italic">
                for colored girls who have considered suicide / when the rainbow
                is enuf
              </em>
              , Douglas Turner Ward's{" "}
              <em className="text-[#FDFBF7] not-italic">Happy Ending</em>, and
              the original work{" "}
              <em className="text-[#FDFBF7] not-italic">Tiny Tim</em>. Audiences
              responded enthusiastically, helping establish both a community for
              the work and an artistic identity for the young organization.
            </Prose>
            <Prose>
              In 2019, the organization incorporated as the{" "}
              <span className="text-[#DE9E48] font-medium">
                Encore School of Arts and Theatre
              </span>
              , reflecting a mission that had begun to extend beyond theatrical
              production to include arts education, artist training, new-work
              development and community engagement.
            </Prose>
            <Prose>
              These early years established principles that continue to guide
              Encore: a commitment to representation, an investment in local
              talent, a belief in the importance of Black cultural institutions
              and an understanding that theatre can serve simultaneously as
              entertainment, education, cultural preservation and community
              gathering.
            </Prose>
          </Section>

          <Section id="identity" num="02" title="Building an Artistic Identity" year="2019–2020">
            <Prose>
              As Encore developed, the theatre established a programming
              identity that embraced the breadth of the African American
              experience rather than limiting Black theatre to a single genre,
              period or subject.
            </Prose>
            <Prose>
              Its stages made room for contemporary and classic drama, comedy,
              musicals, family programming and original work. Stories could be
              historical or contemporary, joyful or difficult, sacred or
              irreverent, traditional or experimental.
            </Prose>
            <Prose>
              That range became central to Encore's understanding of
              representation. The goal was not simply to increase the number of
              Black performers appearing onstage, but to help create an
              environment in which Black artists, stories and audiences could
              participate fully in Birmingham's theatrical life.
            </Prose>
            <Prose>
              Encore also became a creative home for artists at different
              stages of their development. Experienced performers and
              theatre-makers worked alongside emerging artists, community
              performers and people entering the theatre for the first time.
            </Prose>
            <PullQuote>
              The Little Theatre with a Lot of Soul — a phrase that reflected
              the intimacy of its performance environment as well as the
              ambition and cultural character of its work.
            </PullQuote>
          </Section>

          <Section id="education" num="03" title="Arts Education & Youth Development">
            <Prose>
              Education became an important part of Encore's institutional
              mission during its early development.
            </Prose>
            <Prose>
              Among the theatre's first youth initiatives was{" "}
              <span className="text-[#DE9E48] font-medium">Acting Up</span>, a
              theatre camp created to introduce young people to acting,
              performance, movement, collaboration and the creative process.
            </Prose>
            <Prose>
              The program was designed not only to develop performance skills,
              but also to encourage confidence, communication, creativity,
              discipline and self-expression. It reflected Encore's belief that
              sustaining a healthy arts community requires investment in young
              people as well as working artists.
            </Prose>
            <Prose>
              As Encore's educational programming evolved, Acting Up was
              reimagined as{" "}
              <span className="text-[#DE9E48] font-medium">
                ShowUp Drama Camp
              </span>
              . ShowUp continues the purpose established by the original
              program while expanding Encore's commitment to creating
              accessible opportunities for young people to discover theatre,
              develop their creative abilities and experience the collaborative
              nature of the performing arts.
            </Prose>
            <Prose>
              Youth education remains part of Encore's larger institutional
              mission to develop future artists, audiences and creative
              citizens.
            </Prose>
          </Section>

          <Section id="covid" num="04" title="Responding to the COVID-19 Pandemic" year="2020">
            <Prose>
              The COVID-19 pandemic presented one of the first major challenges
              in Encore's young history.
            </Prose>
            <Prose>
              Like theatres across the country, Encore was forced to suspend
              live performances and public gatherings in 2020. For a young
              organization whose artistic and financial model depended heavily
              upon bringing people together for live performance, the shutdown
              created significant uncertainty.
            </Prose>
            <Prose>
              Encore responded by developing{" "}
              <span className="text-[#DE9E48] font-medium">
                Monologue Mondays
              </span>
              , a virtual performance series presented through Facebook Live.
            </Prose>
            <Prose>
              Throughout the series, actors performed material from writers
              including August Wilson, Lorraine Hansberry, Lynn Nottage and
              William Shakespeare. Encore artists were joined by performers
              from outside Birmingham, including participants from Atlanta and
              St. Louis, allowing the theatre's programming to reach beyond the
              physical boundaries of its stage.
            </Prose>
            <Prose>
              The series attracted attention beyond Birmingham and received a
              special video acknowledgment from legendary actress{" "}
              <span className="text-[#FDFBF7] font-medium">Marla Gibbs</span>,
              the five-time Emmy-nominated performer known for her landmark
              roles on <em className="not-italic">The Jeffersons</em> and{" "}
              <em className="not-italic">227</em>. Gibbs' acknowledgment
              celebrated the artists and work being presented through the
              series.
            </Prose>
            <PullQuote>
              Encore's work did not have to be confined to a single stage or
              building.
            </PullQuote>
          </Section>

          <Section id="rebuild" num="05" title="Reopening, Recovery & Growth" year="2021–2024">
            <Prose>
              Following the disruption of the pandemic, Encore resumed live
              performances and entered a period of rebuilding, artistic
              development and organizational growth.
            </Prose>
            <Prose>
              Like many small nonprofit and community-based arts organizations,
              the theatre faced the challenge of rebuilding audiences while
              managing the financial and operational realities of producing
              live theatre in a changed cultural environment.
            </Prose>
            <Prose>
              Encore continued presenting plays, musicals, original productions,
              special events and educational programming while creating
              opportunities for actors, directors, playwrights, designers,
              musicians, technicians, teaching artists and volunteers.
            </Prose>
            <Prose>
              During these years, the theatre strengthened its position as one
              of Alabama's few Black-founded and Black-led brick-and-mortar
              theatre organizations.
            </Prose>
            <Prose>
              The company also continued broadening its artistic reach.
              Encore's commitment to the African American experience remained
              central to its identity while its stages increasingly welcomed
              artists, collaborators and audiences from across Birmingham's
              diverse communities.
            </Prose>
            <PullQuote>
              Encore could remain unapologetically rooted in Black culture
              while creating a welcoming artistic home for people of many
              backgrounds.
            </PullQuote>
          </Section>

          <Section id="original" num="06" title="Original Works & Artist Development">
            <Prose>
              The development of original work has become an important part of
              Encore's artistic identity.
            </Prose>
            <Prose>
              As the organization grew, Encore increasingly provided space for
              playwrights and theatre-makers to develop work that might not
              otherwise have an immediate path from page to stage.
            </Prose>
            <Prose>
              Particular attention has been given to stories connected to
              African American culture, Black Southern life, Birmingham,
              Alabama and the broader experiences of communities that have
              historically had limited opportunities to see themselves
              represented fully in theatrical literature.
            </Prose>
            <Prose>
              Through the development and production of original work, Encore
              has served not only as a presenter of established plays but as a
              place where new stories can be rehearsed, tested, produced and
              introduced to audiences.
            </Prose>
            <PullQuote>
              Communities should not only have opportunities to see themselves
              represented in existing theatrical literature; they should also
              have opportunities to contribute new stories to that literature.
            </PullQuote>
          </Section>

          <Section id="guest" num="07" title="Guest Artists & Expanding Reach">
            <Prose>
              As Encore's programming and profile grew, the theatre began
              welcoming artists whose careers extended beyond Birmingham and
              Alabama.
            </Prose>
            <Prose>
              Nationally recognized performers, actors and recording artists
              associated with Encore programming have included{" "}
              <span className="text-[#FDFBF7] font-medium">Ann Nesby</span>,{" "}
              <span className="text-[#FDFBF7] font-medium">Terrell Carter</span>
              , <span className="text-[#FDFBF7] font-medium">Willie Taylor</span>
              ,{" "}
              <span className="text-[#FDFBF7] font-medium">Dondria Nicole</span>{" "}
              and{" "}
              <span className="text-[#FDFBF7] font-medium">Ray Lavender</span>,
              among others.
            </Prose>
            <Prose>
              Their appearances became part of a larger artistic environment
              that continued to place established performers alongside
              Birmingham artists, emerging talent and young people developing
              their craft.
            </Prose>
            <Prose>
              This combination became an important part of Encore's
              institutional character. National recognition was never intended
              to determine who was worthy of the stage. Rather, the presence
              of established artists demonstrated the theatre's growing ability
              to create programming that connected Birmingham's local creative
              community with artists working on a broader national platform.
            </Prose>
            <Prose>
              Encore continued to maintain that professional artists, community
              artists, emerging performers and young people discovering their
              gifts could all contribute meaningfully to the cultural life of
              the institution.
            </Prose>
          </Section>

          <Section id="transformative" num="08" title="A Home for Transformative Art">
            <Prose>
              As Encore matured, the organization adopted the phrase{" "}
              <span className="text-[#DE9E48] font-medium">
                "A Home for Transformative Art"
              </span>{" "}
              to express the broader purpose of its work.
            </Prose>
            <Prose>
              The theatre's mission grew to encompass several interconnected
              priorities: producing meaningful theatrical experiences,
              amplifying stories from the African diaspora, cultivating local
              talent, developing new work, educating young people, preserving
              culture and creating spaces where Birmingham audiences and
              families could gather.
            </Prose>
            <Prose>
              Encore's identity as an African American founded and led
              institution remains central to that mission.
            </Prose>
            <Prose>
              The organization believes that a culturally specific institution
              can be both firmly rooted in its identity and genuinely inclusive
              in its welcome. Encore's work proudly centers the African
              American experience while inviting artists and audiences across
              race, culture, background, faith and experience to participate in
              the life of the theatre.
            </Prose>
            <PullQuote>
              Not simply a place where performances happen, but a cultural
              resource created by and for the community it serves.
            </PullQuote>
          </Section>

          <Section id="downtown" num="09" title="Relocation to Downtown Birmingham" year="2025">
            <Prose>
              In 2025, Encore entered a significant new chapter with its
              relocation from Roebuck in East Birmingham to{" "}
              <span className="text-[#FDFBF7] font-medium">
                1604 Second Avenue North
              </span>{" "}
              in downtown Birmingham.
            </Prose>
            <Prose>
              The move placed the organization within Birmingham's downtown
              theatre district and represented an important stage in Encore's
              institutional development.
            </Prose>
            <Prose>
              The new home established an intimate{" "}
              <span className="text-[#DE9E48] font-medium">
                99-seat black-box theatre
              </span>{" "}
              while providing additional opportunities for performances,
              rehearsals, education, special events, exhibitions and community
              programming.
            </Prose>
            <Prose>
              The relocation also encouraged Encore to reconsider what growth
              could mean for the organization. Rather than measuring expansion
              solely by the size of a building or auditorium, the theatre
              increasingly focused on expanding the reach, diversity and
              quality of its programming.
            </Prose>
            <Prose>
              Its downtown home provided an environment for theatrical
              productions alongside concerts, cabaret, comedy, educational
              programming, independent projects, gallery activity and community
              events.
            </Prose>
            <Prose>
              The relocation carried particular significance for an organization
              founded to strengthen the presence of Black stories and artists
              within Birmingham's cultural landscape. Encore was establishing
              its next chapter in the center of the city and near several of
              Birmingham's historic performing-arts institutions.
            </Prose>
          </Section>

          <Section id="jazzhall" num="10" title="Alabama Jazz Hall of Fame Partnership" year="2025–Present">
            <Prose>
              Also in 2025, Encore launched a partnership with the{" "}
              <span className="text-[#FDFBF7] font-medium">
                Alabama Jazz Hall of Fame
              </span>{" "}
              to expand theatrical programming beyond Encore's own stage.
            </Prose>
            <Prose>
              The relationship created opportunities for Encore to present
              productions at the historic{" "}
              <span className="text-[#DE9E48] font-medium">Carver Theatre</span>
              , home of the Alabama Jazz Hall of Fame and one of Birmingham's
              culturally significant performance venues.
            </Prose>
            <Prose>
              The partnership represented an important development in Encore's
              producing model. The organization could maintain its own intimate
              theatrical home while also producing work in a larger historic
              venue.
            </Prose>
            <Prose>
              The relationship carried particular cultural significance because
              both institutions share commitments to the preservation,
              presentation and continued development of Black artistic
              expression. The Alabama Jazz Hall of Fame preserves and
              celebrates Alabama's extraordinary contributions to jazz, while
              Encore's work centers theatre, storytelling, education and the
              cultural experiences of the African diaspora.
            </Prose>
            <PullQuote>
              The reach of a theatre does not have to end at the walls of its
              building.
            </PullQuote>
            <Prose>
              Encore's ongoing programming at the Carver Theatre demonstrates
              the continuation of that relationship, with Encore-presented
              theatrical productions appearing within the Jazz Hall's
              performance calendar.
            </Prose>
          </Section>

          <Section id="collaboration" num="11" title="Collaboration Across Birmingham" year="2025–Present">
            <Prose>
              Encore's downtown chapter has also been characterized by a
              growing emphasis on collaboration throughout Birmingham's
              performing-arts community.
            </Prose>
            <Prose>
              The theatre has increasingly worked with fellow arts
              organizations, independent producers, artists, schools, cultural
              institutions and venues to expand opportunities for live
              performance.
            </Prose>
            <Prose>
              By 2026, that collaborative approach included an expanding
              relationship with{" "}
              <span className="text-[#DE9E48] font-medium">
                Birmingham Festival Theatre
              </span>
              , creating opportunities for shared programming, artists,
              audiences, venues and resources while allowing each organization
              to maintain its own artistic identity.
            </Prose>
            <Prose>
              These relationships reflect Encore's belief that a healthy
              theatrical community is strengthened when institutions identify
              opportunities to collaborate.
            </Prose>
            <Prose>
              For Encore, collaboration has also expanded the meaning of what
              it means to be a producing theatre. An Encore project can be
              developed in its own rehearsal rooms and presented on another
              stage. Artists can work across organizations. Audiences can
              discover unfamiliar companies and venues. Resources can be
              shared, and historic performance spaces can remain active through
              new programming.
            </Prose>
            <Prose>
              Encore has consequently continued developing as both a theatrical
              home and a producing organization capable of creating work
              throughout Birmingham.
            </Prose>
          </Section>

          <Section id="season-eight" num="12" title="Season Eight: Still Becoming" year="2026–2027">
            <Prose>
              In 2026, Encore entered its eighth season under the theme{" "}
              <span className="text-[#DE9E48] font-medium italic">
                "Still Becoming."
              </span>
            </Prose>
            <Prose>
              The theme reflected both the artistic programming of the season
              and the organization's understanding of its own development.
            </Prose>
            <Prose>
              Eight years after its founding, Encore had grown from a
              grassroots theatre created to expand opportunities for Black
              artists and stories into an established Birmingham cultural
              organization with a downtown home, educational programming,
              original works, nationally recognized guest artists and growing
              institutional partnerships.
            </Prose>
            <Prose>
              Season Eight continues Encore's commitment to a varied artistic
              program encompassing mainstage productions, studio work, youth
              and family programming, new works and special events.
            </Prose>
            <Prose>
              The season also reflects the reality that institutional
              development is ongoing.
            </Prose>
            <Prose>
              Encore continues working toward greater sustainability, expanded
              artist compensation, stronger organizational infrastructure,
              broader audiences, deeper educational opportunities, new
              partnerships and increased opportunities for Birmingham artists.
            </Prose>
            <PullQuote>
              "Still Becoming" acknowledges that Encore can celebrate its
              history without treating that history as complete.
            </PullQuote>
          </Section>

          <Section id="today" num="13" title="Encore Today">
            <Prose>
              Today, Encore Theatre &amp; Gallery is a Birmingham-based
              community theatre and arts organization dedicated to ensuring
              that stories and voices from the African diaspora remain a vital
              part of the city's cultural landscape. The organization describes
              itself as{" "}
              <span className="text-[#DE9E48] italic">
                "The Little Theatre with a Lot of Soul"
              </span>{" "}
              and{" "}
              <span className="text-[#DE9E48] italic">
                "A Home for Transformative Art."
              </span>
            </Prose>
            <Prose>
              Encore operates from its downtown home at 1604 Second Avenue
              North and continues to produce theatre while supporting
              education, artist development, new works, multidisciplinary
              programming and community engagement.
            </Prose>
            <Prose>
              Its history has been shaped by thousands of individual
              contributions: actors and directors, playwrights and musicians,
              designers and technicians, teaching artists and students, staff
              and volunteers, donors and community partners, families and
              audiences.
            </Prose>
            <Prose>
              Some have participated for years. Others have contributed to a
              single production, class or event. Together, they form the
              institutional history of Encore.
            </Prose>
            <Prose>
              Since 2018, the organization has survived the challenges of
              establishing a new Black-led theatre, an unprecedented global
              pandemic, the rebuilding of live audiences and a major
              relocation. Along the way, Encore has expanded its educational
              work, developed original productions, welcomed nationally
              recognized artists, created new partnerships and established a
              presence in downtown Birmingham.
            </Prose>
            <Prose>
              What began as an effort to make more room for Black artists,
              stories and audiences has grown into a broader commitment to
              uplift powerful stories, cultivate local talent, give young
              people a creative path forward, preserve culture and create a
              place where community can gather. Those priorities remain part
              of Encore's stated mission today.
            </Prose>
            <Prose>
              Encore remains proudly rooted in the African American experience
              while welcoming the larger community into the work.
            </Prose>
            <div className="border-l-2 border-[#DE9E48] pl-6 py-2 space-y-1 font-display italic text-xl md:text-2xl leading-snug text-[#FDFBF7] mt-6">
              <p>Its history is still being written.</p>
              <p>Its mission remains active.</p>
              <p>And its commitment to Birmingham remains at the center of what comes next.</p>
            </div>
            <div className="mt-10 text-center border-t border-white/10 pt-10">
              <p className="font-display text-2xl md:text-3xl text-[#FDFBF7] tracking-tight">
                Encore Theatre &amp; Gallery
              </p>
              <p className="font-body italic text-[#DE9E48] mt-2">
                A Home for Transformative Art.
              </p>
              <p className="font-body italic text-stone-400 text-sm mt-1">
                The Little Theatre with a Lot of Soul.
              </p>
            </div>
          </Section>

          {/* TIMELINE — visual vertical timeline */}
          <Section id="timeline" num="14" title="Institutional Timeline">
            <div className="relative pl-8 md:pl-12">
              {/* Vertical line */}
              <div className="absolute left-2 md:left-3 top-2 bottom-2 w-[2px] bg-gradient-to-b from-[#DE9E48] via-[#DE9E48] to-[#DE9E48]" />
              <ul className="space-y-10">
                {TIMELINE_ENTRIES.map((entry, idx) => (
                  <li key={entry.year + entry.title} className="relative">
                    {/* Dot */}
                    <div
                      className={`absolute -left-[26px] md:-left-[38px] top-1.5 w-3 h-3 rounded-full ring-4 ring-stone-950 ${
                        idx === TIMELINE_ENTRIES.length - 1
                          ? "bg-[#DE9E48] shadow-[0_0_15px_rgba(222,158,72,0.7)]"
                          : "bg-[#DE9E48]"
                      }`}
                    />
                    <p className="font-overline text-[0.65rem] tracking-[0.24em] text-[#DE9E48] mb-2 tabular-nums">
                      {entry.year}
                    </p>
                    <h3 className="font-display text-xl md:text-2xl tracking-tight text-[#FDFBF7] mb-2 leading-tight">
                      {entry.title}
                    </h3>
                    <p className="font-body text-[0.98rem] leading-[1.75] text-stone-300 max-w-2xl">
                      {entry.body}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </Section>

          {/* Back to top */}
          <div className="pt-8 border-t border-white/10 flex justify-between items-center print:hidden">
            <p className="font-overline text-[0.6rem] tracking-[0.24em] text-stone-500">
              — End of History · Season 8
            </p>
            <a
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              data-testid="history-back-to-top"
              className="inline-flex items-center gap-2 font-overline text-[0.65rem] tracking-[0.22em] text-stone-400 hover:text-[#DE9E48] transition-colors"
            >
              <ArrowUp size={14} /> Back to top
            </a>
          </div>
        </main>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Sub-components — same idiom as Policies                                    */
/* -------------------------------------------------------------------------- */

function Section({ id, num, title, year, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <div className="mb-10">
        <p className="font-overline text-[0.62rem] tracking-[0.28em] text-[#DE9E48] mb-3 tabular-nums">
          — Chapter {num}
          {year ? ` · ${year}` : ""}
        </p>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-[#FDFBF7] mb-3">
          {title}
        </h2>
        <div className="mt-6 w-16 h-[2px] bg-[#DE9E48]" />
      </div>
      <div className="space-y-6 text-[1.02rem] text-stone-300">{children}</div>
    </section>
  );
}

function Prose({ children }) {
  return <p className="leading-[1.8]">{children}</p>;
}

function PullQuote({ children }) {
  return (
    <blockquote className="border-l-2 border-[#DE9E48] pl-6 py-1 my-4 font-display italic text-xl md:text-2xl leading-snug text-[#FDFBF7]">
      {children}
    </blockquote>
  );
}
